import { Card, Text, Box, Flex, Image } from "@chakra-ui/react";
import emoji from 'react-easy-emoji'

// Utils
import bgSelector from "./utils/bg-selector";
import { getFonts } from "./utils/get-fonts";

// Types
import type { Carousel, Settings, Theme } from "types/carousel";

const fontSizes = {
  "small": {
    "number": 1,
    "title": 2,
    "text": 0.875,
  },
  "medium": {
    "number": 1.5,
    "title": 3,
    "text": 1,
  },
  "large": {
    "number": 2,
    "title": 4,
    "text": 1.5,
  }
}

const imageAlignments = {
  "left": "flex-start",
  "center": "center",
  "right": "flex-end",
}

const Preview = ({
  content,
  settings,
  theme,
  slideNo,
  scale = 1,
  thumbnail = undefined,
}: {
  content: Carousel;
  settings: Settings;
  theme: Theme;
  slideNo?: number;
  scale?: number;
  thumbnail?: {
    setselectedCarouselId: (id: string) => void;
    selectedCarouselId: string;
  };
}) => {
  const {
    type,
    title,
    titleActive,
    text,
    textActive,
    image,
    imagePosition = "top",
    imageAspectRatio = "original",
    imageWidth = 30,
    imageStyle = "rounded",
    imageAlign = "left",
  } = content;
  const thumbnailProps = thumbnail && {
    onClick: () => thumbnail.setselectedCarouselId(content.id),
    role: "button",
    opacity: thumbnail.selectedCarouselId === content.id ? 1 : 0.5,
    sx: { userSelect: "none" },
  };
  const { titleFontFamily, titleFontWeight, textFontFamily, textFontWeight } = getFonts(settings.fonts);
  const hasImage = Boolean(image);
  const hasOriginalAspectRatio = imageAspectRatio === "original";
  let imageBorderRadius;
  switch (imageStyle) {
    case "rounded":
      imageBorderRadius = `${0.75 * scale}rem`;
      break;
    case "circle":
      imageBorderRadius = "50%";
      break;
    default:
      imageBorderRadius = undefined;
  }
  return (
    <Card
      h={"100%"}
      w={"100%"}
      boxSizing="border-box"
      bg={
        theme.background.active
          ? `url("data:image/svg+xml;utf8,${encodeURIComponent(
            bgSelector(theme.colors, theme.background.type)
          )}") ${theme.colors.accent} center/cover`
          : theme.colors.accent
      }
      overflow={"hidden"}
      p={`${scale * 2}rem`}
      display={"grid"}
      gridTemplateRows={`${scale * 3}rem auto ${scale * 3}rem`}
      gap={`${scale * 0.75}rem`}
      aspectRatio={"4/5"}
      position={"relative"}
      textAlign={"left"}
      userSelect={"none"}
      {...thumbnailProps}

    >
      <Flex gridRowStart={hasImage ? 1 : 2} gridRowEnd={3} gap={`${scale * 1}rem`} my={hasImage ? 0 : "auto"} direction="column" justifyContent={"center"}>
        <Box order={imagePosition === "bottom" ? 1 : 2} >
          {Boolean(slideNo) && type === "content" && settings.showSlideNumbers && (
            <Flex
              bg={theme.colors.secondary}
              borderRadius={"50%"}
              w={`${scale * (fontSizes[settings?.fonts?.size || "medium"]?.title)}rem`}
              h={`${scale * (fontSizes[settings?.fonts?.size || "medium"]?.title)}rem`}
              alignItems={"center"}
              justifyContent={"center"}
              mb={`${scale * 1}rem`}
            >
              <Text
                as="span"
                fontSize={`${scale * (fontSizes[settings?.fonts?.size || "medium"]?.number)}rem`}
                color={theme.colors.accent}
                m={0}
                fontWeight={titleFontWeight}
                fontFamily={titleFontFamily}
                lineHeight={1}
              >
                {slideNo}
              </Text>
            </Flex>
          )}
          {titleActive && (
            <Text
              as="span"
              display="block"
              fontSize={`${scale * (fontSizes[settings?.fonts?.size || "medium"]?.title)}rem`}
              m={0}
              mb={`${1 * scale}rem`}
              color={theme.colors.primary}
              fontWeight={titleFontWeight}
              fontFamily={titleFontFamily}
              lineHeight={1}
            >

              {emoji(title,
                (code, string, offset) => {
                  return <Image display="inline" height={`${scale * (fontSizes[settings?.fonts?.size || "medium"]?.title)}rem`} src={`https://rawcdn.githack.com/twitter/twemoji/d94f4cf793e6d5ca592aa00f58a88f6a4229ad43/assets/svg/${code}.svg`} alt={string} key={offset} />
                }
              )}
            </Text>
          )}
          {textActive && (
            <Text
              as="span"
              display="block"
              fontSize={`${scale * (fontSizes[settings?.fonts?.size || "medium"]?.text)}rem`}
              color={theme.colors.secondary}
              m={0}
              whiteSpace={"pre-wrap"}
              fontWeight={textFontWeight}
              fontFamily={textFontFamily}
              lineHeight={1.3}
            >
              {emoji(text, (code, string, offset) => {
                return <Image display="inline" height={`${scale * 1}rem`} src={`https://rawcdn.githack.com/twitter/twemoji/d94f4cf793e6d5ca592aa00f58a88f6a4229ad43/assets/svg/${code}.svg`} alt={string} key={offset} />
              })}
            </Text>
          )}
        </Box>
        {hasImage && <Flex justifyContent={imageAlignments[imageAlign]} overflow="hidden" order={imagePosition === "bottom" ? 2 : 1} >
          <Image src={image} alt={"Slide"}
            w={`${imageWidth}%`}
            aspectRatio={hasOriginalAspectRatio ? undefined : imageAspectRatio}
            objectFit={hasOriginalAspectRatio ? undefined : "cover"}
            objectPosition={hasOriginalAspectRatio ? undefined : "center"}
            borderRadius={imageBorderRadius}
          />
        </Flex>}
      </Flex>
      <Box
        gridRowStart={3}
        gridRowEnd={4}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"flex-start"}
        gap={`${scale * 0.5}rem`}
      >
        {settings.headshotActive && (
          <img
            src={settings.headshot}
            alt={"Headshot"}
            style={{
              width: `${scale * 3}rem`,
              height: `${scale * 3}rem`,
              borderRadius: "50%",
            }}
          />
        )}
        <Box>
          {settings.nameActive && (
            <Text
              as="span"
              display="block"
              fontSize={`${scale * 0.875}rem`}
              color={theme.colors.primary}
              fontWeight={titleFontWeight}
              fontFamily={titleFontFamily}
              m={0}
            >
              {settings.name}
            </Text>
          )}
          {settings.handleActive && (
            <Text
              as="span"
              display="block"
              fontSize={`${scale * 0.75}rem`}
              color={theme.colors.secondary}
              fontWeight={textFontWeight}
              fontFamily={textFontFamily}
              m={0}
            >
              @{settings.handle}
            </Text>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default Preview;
